/**
 * ProductDetailModal V2 - Quick View für Produkte
 *
 * Zeigt Produktdetails in einem Modal. Für die vollständige Ansicht
 * wird auf /v2/products/:productId verwiesen.
 */
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  X,
  ExternalLink,
  RefreshCw,
  Image as ImageIcon,
  ZoomIn,
  Loader2,
  Info,
  Code,
  Images,
  AlertCircle,
} from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { getProductImages, syncProductImages } from '@/lib/api/products';
import type { Product, ProductImage } from '@/types/products';
import { Button, Badge, Tabs, TabsList, TabsTrigger, TabsContent, Alert, AlertDescription } from '@/components/v2/ui';
import { AIFieldButton } from '@/components/v2/products/ai-field';
import { cn, formatDateTime } from '@/lib/utils';

export interface ProductDetailModalProps {
  show: boolean;
  product: Product | null;
  onClose: () => void;
  initialTab?: 'details' | 'images' | 'raw';
}

export function ProductDetailModal({
  show,
  product,
  onClose,
  initialTab = 'details',
}: ProductDetailModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [images, setImages] = useState<ProductImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<ProductImage | null>(null);
  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const [lightboxImage, setLightboxImage] = useState<ProductImage | null>(null);

  const loadAllImages = useCallback(async () => {
    if (!product) return;

    setLoading(true);
    setError(null);

    try {
      const response = await getProductImages(product.id);
      if (response.success) {
        setImages(response.images);
        if (response.images.length > 0) {
          setSelectedImage(response.images[0]);
        }
      }
    } catch (err) {
      console.error('Error loading images:', err);
      setError('Fehler beim Laden der Bilder');
    } finally {
      setLoading(false);
    }
  }, [product]);

  // Lade alle Bilder wenn Modal geöffnet wird
  useEffect(() => {
    if (show && product) {
      loadAllImages();
      setActiveTab(initialTab);
    }
  }, [show, product, initialTab, loadAllImages]);

  // Reset wenn geschlossen
  useEffect(() => {
    if (!show) {
      setImages([]);
      setSelectedImage(null);
      setError(null);
      setLightboxImage(null);
    }
  }, [show]);

  const handleSyncImages = async (onlyFirst: boolean = false) => {
    if (!product) return;

    setSyncing(true);
    setError(null);

    try {
      const response = await syncProductImages(product.id, onlyFirst);
      if (response.success) {
        await loadAllImages();
      } else {
        setError(response.error || 'Sync fehlgeschlagen');
      }
    } catch (err) {
      console.error('Error syncing images:', err);
      setError(t('errors.syncing'));
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return formatDateTime(dateString);
  };

  const handleOpenFullView = () => {
    if (product) {
      onClose();
      navigate(`/v2/products/${product.id}`);
    }
  };

  if (!product || !show) return null;

  return (
    <>
      <Dialog.Root open={show} onOpenChange={(open) => !open && onClose()}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/60 z-50" />
          <Dialog.Content
            className={cn(
              'fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
              'w-full max-w-4xl max-h-[90vh]',
              'bg-white dark:bg-neutral-900 rounded-xl shadow-xl overflow-hidden',
              'flex flex-col',
              'focus:outline-none'
            )}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Dialog.Title className="text-lg font-semibold text-neutral-900 dark:text-white">
                  {product.name}
                </Dialog.Title>
                <Badge variant="secondary">Quick View</Badge>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleOpenFullView}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Vollständige Ansicht
                </Button>
                <Dialog.Close asChild>
                  <Button variant="ghost" size="sm">
                    <X className="h-4 w-4" />
                  </Button>
                </Dialog.Close>
              </div>
            </div>

            {/* Hint */}
            <div className="px-6 py-3 bg-primary-50 dark:bg-primary-900/20 border-b border-neutral-200 dark:border-neutral-700">
              <p className="text-sm text-primary-700 dark:text-primary-300 flex items-center gap-2">
                <Info className="h-4 w-4 flex-shrink-0" />
                Für Content, Assets, Channels und mehr nutze die vollständige Ansicht.
              </p>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
              <div className="px-6 pt-4">
                <TabsList>
                  <TabsTrigger value="details" className="flex items-center gap-2">
                    <Info className="h-4 w-4" />
                    Details
                  </TabsTrigger>
                  <TabsTrigger value="images" className="flex items-center gap-2">
                    <Images className="h-4 w-4" />
                    Bilder ({images.length})
                  </TabsTrigger>
                  <TabsTrigger value="raw" className="flex items-center gap-2">
                    <Code className="h-4 w-4" />
                    Raw Data
                  </TabsTrigger>
                </TabsList>
              </div>

              {/* Tab Content */}
              <div className="flex-1 overflow-y-auto px-6 py-4">
                {/* Details Tab */}
                <TabsContent value="details">
                  <div className="grid md:grid-cols-5 gap-6">
                    {/* Bild links */}
                    <div className="md:col-span-2">
                      <div className="aspect-square bg-neutral-100 dark:bg-neutral-800 rounded-lg flex items-center justify-center relative overflow-hidden">
                        {loading ? (
                          <Loader2 className="h-8 w-8 animate-spin text-neutral-400" />
                        ) : selectedImage?.thumbnail_url ? (
                          <img
                            src={selectedImage.thumbnail_url}
                            alt={product.name}
                            className="max-w-full max-h-full object-contain"
                          />
                        ) : (
                          <div className="text-center">
                            <ImageIcon className="h-12 w-12 text-neutral-400 mx-auto mb-2" />
                            <p className="text-sm text-neutral-500">Kein Bild</p>
                            <Button
                              variant="outline"
                              size="sm"
                              className="mt-3"
                              onClick={() => handleSyncImages(true)}
                              disabled={syncing}
                            >
                              {syncing ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <RefreshCw className="h-4 w-4 mr-2" />
                              )}
                              Bild laden
                            </Button>
                          </div>
                        )}

                        {/* Sync Status Badge */}
                        {selectedImage && (
                          <Badge
                            variant={
                              selectedImage.sync_status === 'synced'
                                ? 'success'
                                : selectedImage.sync_status === 'error'
                                ? 'error'
                                : 'warning'
                            }
                            className="absolute top-2 right-2"
                          >
                            {selectedImage.sync_status === 'synced'
                              ? 'Synchronisiert'
                              : selectedImage.sync_status === 'error'
                              ? 'Fehler'
                              : 'Ausstehend'}
                          </Badge>
                        )}
                      </div>

                      {/* Thumbnails */}
                      {images.length > 1 && (
                        <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
                          {images.map((img) => (
                            <button
                              key={img.id}
                              className={cn(
                                'w-12 h-12 rounded border-2 overflow-hidden flex-shrink-0',
                                selectedImage?.id === img.id
                                  ? 'border-primary-500'
                                  : 'border-transparent hover:border-neutral-300'
                              )}
                              onClick={() => setSelectedImage(img)}
                            >
                              {img.thumbnail_url ? (
                                <img
                                  src={img.thumbnail_url}
                                  alt={`Bild ${img.sort_number}`}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full bg-neutral-200 flex items-center justify-center">
                                  <ImageIcon className="h-4 w-4 text-neutral-400" />
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Infos rechts */}
                    <div className="md:col-span-3 space-y-3">
                      <InfoRow label="SKU" value={<code className="bg-neutral-100 dark:bg-neutral-800 px-2 py-1 rounded text-sm">{product.sku || '-'}</code>} />
                      <InfoRow label="GTIN/EAN" value={product.gtin || '-'} />
                      <InfoRow label="Externe ID" value={<code className="bg-neutral-100 dark:bg-neutral-800 px-2 py-1 rounded text-sm">{product.external_id}</code>} />
                      <InfoRow
                        label="Status"
                        value={
                          <Badge variant={product.is_active ? 'success' : 'secondary'}>
                            {product.is_active ? 'Aktiv' : 'Inaktiv'}
                          </Badge>
                        }
                      />
                      {product.price_net != null && (
                        <InfoRow label="Preis (netto)" value={`${product.price_net.toFixed(2)} €`} />
                      )}
                      {product.price_gross != null && (
                        <InfoRow label="Preis (brutto)" value={`${product.price_gross.toFixed(2)} €`} />
                      )}
                      <InfoRow label="Synchronisiert" value={formatDate(product.synced_at)} />
                      <InfoRow
                        label="Provider"
                        value={
                          <Badge variant="info">
                            {product.provider_type?.toUpperCase() || 'JTL'}
                          </Badge>
                        }
                      />

                      <div className="pt-3 border-t border-neutral-200 dark:border-neutral-700">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                            {t('aiField.descriptionLabel')}
                          </p>
                          <AIFieldButton productId={product.id} fieldType="product_description" />
                        </div>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400">
                          {product.description || t('aiField.noDescription')}
                        </p>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Images Tab */}
                <TabsContent value="images">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm text-neutral-500">
                      {images.length} Bild{images.length !== 1 ? 'er' : ''} synchronisiert
                    </span>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={loadAllImages} disabled={loading}>
                        <RefreshCw className={cn('h-4 w-4 mr-2', loading && 'animate-spin')} />
                        {t('common.update')}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleSyncImages(false)} disabled={syncing}>
                        {syncing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Alle synchronisieren
                      </Button>
                    </div>
                  </div>

                  {loading ? (
                    <div className="py-12 text-center">
                      <Loader2 className="h-8 w-8 animate-spin text-primary-500 mx-auto mb-3" />
                      <p className="text-neutral-500">Lade Bilder...</p>
                    </div>
                  ) : images.length === 0 ? (
                    <div className="py-12 text-center">
                      <Images className="h-12 w-12 text-neutral-400 mx-auto mb-3" />
                      <p className="text-neutral-500 mb-4">Keine Bilder synchronisiert</p>
                      <Button onClick={() => handleSyncImages(false)} disabled={syncing}>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Bilder synchronisieren
                      </Button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-4">
                      {images.map((img) => (
                        <div
                          key={img.id}
                          className={cn(
                            'aspect-square bg-neutral-100 dark:bg-neutral-800 rounded-lg relative overflow-hidden group',
                            img.s3_url && 'cursor-pointer'
                          )}
                          onClick={() => img.s3_url && setLightboxImage(img)}
                        >
                          {img.thumbnail_url ? (
                            <img
                              src={img.thumbnail_url}
                              alt={`Bild ${img.sort_number}`}
                              className="w-full h-full object-contain p-2"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <ImageIcon className="h-8 w-8 text-neutral-400" />
                            </div>
                          )}

                          {/* Badges */}
                          <Badge
                            variant="secondary"
                            className="absolute top-2 left-2"
                          >
                            #{img.sort_number}
                          </Badge>

                          <Badge
                            variant={
                              img.sync_status === 'synced'
                                ? 'success'
                                : img.sync_status === 'error'
                                ? 'error'
                                : 'warning'
                            }
                            className="absolute top-2 right-2"
                          >
                            {img.sync_status === 'synced' ? '✓' : img.sync_status === 'error' ? '✗' : '⏳'}
                          </Badge>

                          {/* Zoom overlay */}
                          {img.s3_url && (
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <ZoomIn className="h-8 w-8 text-white" />
                            </div>
                          )}

                          {/* Dimensions */}
                          {img.width && img.height && (
                            <Badge variant="secondary" className="absolute bottom-2 right-2 text-xs">
                              {img.width}×{img.height}
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Raw Data Tab */}
                <TabsContent value="raw">
                  <div className="bg-neutral-900 text-neutral-100 p-4 rounded-lg overflow-x-auto">
                    <pre className="text-sm font-mono">
                      {JSON.stringify(product.raw_data, null, 2)}
                    </pre>
                  </div>
                </TabsContent>
              </div>
            </Tabs>

            {/* Error */}
            {error && (
              <div className="px-6 py-3 border-t border-neutral-200 dark:border-neutral-700">
                <Alert variant="error">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              </div>
            )}

            {/* Footer */}
            <div className="px-6 py-4 border-t border-neutral-200 dark:border-neutral-700 flex justify-between">
              <Button variant="outline" onClick={handleOpenFullView}>
                <ExternalLink className="h-4 w-4 mr-2" />
                In neuer Ansicht öffnen
              </Button>
              <Button variant="secondary" onClick={onClose}>
                {t('common.close')}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Lightbox */}
      {lightboxImage?.s3_url && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center cursor-pointer"
          onClick={() => setLightboxImage(null)}
        >
          <button
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            onClick={() => setLightboxImage(null)}
          >
            <X className="h-6 w-6" />
          </button>

          <div className="absolute top-4 left-4 text-white">
            <Badge variant="secondary">
              Bild #{lightboxImage.sort_number}
              {lightboxImage.width && lightboxImage.height && (
                <> · {lightboxImage.width}×{lightboxImage.height}</>
              )}
            </Badge>
          </div>

          <img
            src={lightboxImage.s3_url}
            alt={`Original Bild ${lightboxImage.sort_number}`}
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          <p className="absolute bottom-4 text-white/60 text-sm">
            Klicken oder ESC zum Schließen
          </p>
        </div>
      )}
    </>
  );
}

interface InfoRowProps {
  label: string;
  value: React.ReactNode;
}

function InfoRow({ label, value }: InfoRowProps) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-neutral-100 dark:border-neutral-800">
      <span className="text-sm text-neutral-500 dark:text-neutral-400">{label}</span>
      <span className="text-sm font-medium text-neutral-900 dark:text-white">{value}</span>
    </div>
  );
}
